import { useRef, useState } from "react";
import { Camera, Loader2, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/ProfileMenu";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BTN, BTN_GHOST, DIALOG, isCancel } from "@/components/account/ConfirmIdentity";

export const Section = ({ title, text, testid, children }) => (
  <section className="soft-card p-5 sm:p-6 space-y-5" data-testid={testid}>
    <div><h2 className="font-display text-lg font-medium">{title}</h2>{text && <p className="text-sm text-neutral-500 mt-1">{text}</p>}</div>
    {children}
  </section>
);

function PhotoRow({ account, onChanged }) {
  const { user, setUser } = useAuth();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); setUser((u) => ({ ...u, ...data })); onChanged(); toast.success(msg); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Please choose a photo under 5 MB."); return; }
    const fd = new FormData();
    fd.append("file", f);
    run(() => http.post("/account/photo", fd), "Photo updated");
  };
  return (
    <div className="flex items-center gap-4">
      <Avatar user={user} className="h-16 w-16 text-lg" />
      <div className="flex flex-wrap gap-2">
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pick} data-testid="account-photo-input" />
        <button onClick={() => ref.current?.click()} disabled={busy} className={BTN_GHOST} data-testid="account-photo-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Change photo
        </button>
        {account.has_photo && <button onClick={() => run(() => http.delete("/account/photo"), "Photo removed")} disabled={busy} className={BTN_GHOST} data-testid="account-photo-remove-btn"><Trash2 className="h-4 w-4" />Remove</button>}
      </div>
    </div>
  );
}

function NameRow() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await http.patch("/account", { name }); setUser((u) => ({ ...u, ...data })); toast.success("Name saved"); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <label className="block">
      <span className="text-sm text-neutral-400">Display name</span>
      <div className="flex gap-2 mt-1.5">
        <Input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10" data-testid="account-name-input" />
        {name.trim() && name.trim() !== user?.name && <button onClick={save} disabled={busy} className={BTN} data-testid="account-name-save-btn">Save</button>}
      </div>
    </label>
  );
}

function EmailDialog({ getToken, onClose, onChanged }) {
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);
  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const token = await getToken(); const { data } = await http.post("/account/email/start", { new_email: email, reauth_token: token }); setSentTo(data.sent_to); }
    catch (err) { if (!isCancel(err)) toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await http.post("/account/email/confirm", { code }); setUser((u) => ({ ...u, ...data })); onChanged(); toast.success("Email address changed"); onClose(); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={DIALOG} data-testid="email-change-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Change email address</DialogTitle>
          <DialogDescription className="text-neutral-400">{sentTo ? `Enter the 6-digit code we sent to ${sentTo}.` : "We'll send a code to the new address to confirm it's yours."}</DialogDescription>
        </DialogHeader>
        {!sentTo ? (
          <form onSubmit={start} className="space-y-3">
            <Input autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="New email address" className="bg-white/5 border-white/10" data-testid="email-change-input" />
            <button type="submit" disabled={busy || !email.trim()} className={BTN} data-testid="email-change-send-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send code</button>
          </form>
        ) : (
          <form onSubmit={confirm} className="space-y-3">
            <Input autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="bg-white/5 border-white/10 tracking-widest" data-testid="email-change-code-input" />
            <button type="submit" disabled={busy || code.length < 6} className={BTN} data-testid="email-change-confirm-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Confirm new email</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EmailRow({ account, getToken, onChanged }) {
  const [open, setOpen] = useState(false);
  let note = "";
  if (account.is_owner) note = "This is the owner address, so it can't be changed here.";
  else if (!account.has_password) note = "Add a password below before changing your email.";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm text-neutral-400">Email</p>
        <p className="text-sm text-neutral-100 truncate flex items-center gap-2" data-testid="account-email"><Mail className="h-4 w-4 text-neutral-500" />{account.email}</p>
        {note && <p className="text-xs text-neutral-500 mt-1" data-testid="account-email-note">{note}</p>}
      </div>
      {!note && <button onClick={() => setOpen(true)} className={BTN_GHOST} data-testid="account-email-change-btn">Change email</button>}
      {open && <EmailDialog getToken={getToken} onClose={() => setOpen(false)} onChanged={onChanged} />}
    </div>
  );
}

export function ProfileSection({ account, getToken, onChanged }) {
  return (
    <Section title="Profile" text="How you appear in BrynAI and in your study groups." testid="account-profile">
      <PhotoRow account={account} onChanged={onChanged} />
      <NameRow />
      <EmailRow account={account} getToken={getToken} onChanged={onChanged} />
    </Section>
  );
}
